// Page PUBLIQUE du questionnaire de mesure zéro — le client y répond sans
// compte, via le lien personnel qu'on lui a envoyé.
//
// Elle est rendue AVANT la vérification de session (voir `App.tsx`) : le
// répondant n'est pas un utilisateur de Zygosight. Le jeton de l'URL est la
// seule autorisation, et il n'ouvre que ce questionnaire.
//
// Trois temps :
//   1. le CONTEXTE — qui demande, pourquoi, et que rien n'est obligatoire ;
//   2. les questions ;
//   3. le REMERCIEMENT, avec la possibilité de demander un accès au
//      référentiel de recherche (accordé ou non par un administrateur : un accès
//      donne à voir des verbatims, on ne l'ouvre pas d'office).
import { Link } from '../router';
import { LegalFooter } from '../legal/LegalPages';
import { useEffect, useState, type FormEvent } from 'react';
import { usePublicQuestionnaire, useRequestAccess, useSubmitPublicQuestionnaire } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { Mesure0Answers } from '../api/types';
import { Button, ErrorBanner, Eyebrow, Field, Spinner } from '../components/ui';
import { TextArea } from '../write/Forms';
import './mesure0.css';

export function PublicQuestionnaire({ token }: { token: string }) {
  const q = usePublicQuestionnaire(token);
  const submit = useSubmitPublicQuestionnaire(token);
  const [answers, setAnswers] = useState<Mesure0Answers>({});
  const [name, setName] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (q.data) {
      setAnswers(q.data.answers);
      setName(q.data.respondentName);
    }
  }, [q.data]);

  // Un jeton dans l'URL ne doit pas fuiter vers un site tiers via l'en-tête
  // Referer, si le répondant clique un lien depuis cette page.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await submit.mutateAsync({ answers, respondentName: name.trim() });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      /* rendu par le bandeau */
    }
  }

  if (q.isPending) {
    return (
      <div className="app-loading">
        <Spinner label="Chargement du questionnaire…" />
      </div>
    );
  }

  if (q.isError) {
    return (
      <main className="m0-public">
        <Eyebrow>Questionnaire</Eyebrow>
        <h1 className="t-h2">Ce lien n’est plus valide</h1>
        <p className="card-lede">
          {q.error instanceof ApiRequestError && q.error.status === 404
            ? 'Il a peut-être expiré ou été remplacé. Demandez-en un nouveau à la personne qui vous l’a envoyé.'
            : 'Le serveur est momentanément injoignable. Réessayez dans un instant.'}
        </p>
      </main>
    );
  }

  const d = q.data;
  const sender = d.senderName ?? 'L’équipe de recherche';
  const answered = Object.values(answers).filter((v) => v.trim()).length;

  if (done) {
    return (
      <ThankYou
        token={token}
        sender={sender}
        organizationName={d.organizationName}
        projectName={d.projectName}
        respondentName={name.trim()}
        answered={answered}
        onEdit={() => setDone(false)}
      />
    );
  }

  return (
    <main className="m0-public">
      <header className="m0-public-head">
        <Eyebrow>Questionnaire · état des lieux</Eyebrow>
        <h1 className="t-h1">{d.projectName}</h1>
      </header>

      {/* Le contexte d'abord : on répond mieux quand on sait à qui, et pourquoi. */}
      <section className="m0-context" aria-labelledby="m0-context-title">
        <h2 className="t-h3" id="m0-context-title">Pourquoi ce questionnaire ?</h2>
        <p>
          <strong>{sender}</strong>
          {d.organizationName ? <> ({d.organizationName})</> : null} prépare une étude sur{' '}
          <strong>{d.projectName}</strong>. Vos réponses l’aident à <strong>mieux cadrer le besoin
          initial</strong> : la mission du produit, ses utilisateurs, vos priorités et les difficultés
          déjà connues — avant d’aller à la rencontre des utilisateurs.
        </p>
        <ul className="m0-context-points">
          <li><strong>Rien n’est obligatoire.</strong> Vos réponses sont indicatives : répondez à ce que vous savez, en quelques mots.</li>
          <li>Comptez une quinzaine de minutes. Vous pouvez revenir compléter vos réponses avec le même lien.</li>
          <li>Vos réponses ne sont lues que par l’équipe de recherche du projet.</li>
          <li>
            Elles sont conservées pour le compte de {d.organizationName || sender}, qui en est responsable : c’est à
            elle que s’adressent vos demandes d’accès ou de suppression. Voir la{' '}
            <Link to="/confidentialite">politique de confidentialité</Link>.
          </li>
        </ul>
      </section>

      <form className="write-form m0-questions" onSubmit={onSubmit} noValidate>
        <Field
          label="Votre nom et votre rôle"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Claire Martin, Product Owner"
        />
        {d.questions.map((question, i) => (
          <TextArea
            key={question.key}
            label={`${i === 0 ? '' : `${i}. `}${question.label}`}
            value={answers[question.key] ?? ''}
            rows={3}
            onChange={(v) => setAnswers((a) => ({ ...a, [question.key]: v }))}
          />
        ))}
        {submit.error ? (
          <ErrorBanner>
            {submit.error instanceof ApiRequestError ? submit.error.message : 'L’envoi a échoué.'}
          </ErrorBanner>
        ) : null}
        <Button type="submit" variant="accent" loading={submit.isPending} disabled={answered === 0}>
          {d.submittedAt ? 'Mettre à jour mes réponses' : 'Envoyer mes réponses'}
        </Button>
        <p className="zy-texte-muet">
          {answered}/{d.questions.length} question{d.questions.length > 1 ? 's' : ''} renseignée{answered > 1 ? 's' : ''}
          {answered === 0 ? ' — répondez à au moins une question pour envoyer.' : '.'}
        </p>
      </form>
      <LegalFooter compact />
    </main>
  );
}

// --- Après l'envoi : remercier, et proposer de suivre la recherche -------------

function ThankYou({
  token, sender, organizationName, projectName, respondentName, answered, onEdit,
}: {
  token: string; sender: string; organizationName: string | null; projectName: string;
  respondentName: string; answered: number; onEdit: () => void;
}) {
  const request = useRequestAccess(token);
  const [email, setEmail] = useState('');
  const [closed, setClosed] = useState(false);

  async function ask(e: FormEvent) {
    e.preventDefault();
    try {
      await request.mutateAsync({ email: email.trim(), name: respondentName || undefined });
    } catch {
      /* rendu par le bandeau */
    }
  }

  // Un onglet ouvert depuis un e-mail ne peut pas toujours être fermé par
  // script : le navigateur l'interdit. On tente, puis on le dit simplement.
  function close() {
    window.close();
    setClosed(true);
  }

  return (
    <main className="m0-public">
      <section className="m0-thanks" aria-labelledby="m0-thanks-title">
        <span className="m0-thanks-mark" aria-hidden="true">✓</span>
        <h1 className="t-h2" id="m0-thanks-title">Merci{respondentName ? `, ${respondentName.split(',')[0]}` : ''} !</h1>
        <p>
          {answered > 1 ? `Vos ${answered} réponses sur ` : 'Votre réponse sur '}
          <strong>{projectName}</strong>
          {answered > 1 ? ' sont bien transmises' : ' est bien transmise'} à {sender}
          {organizationName ? ` (${organizationName})` : ''}.{' '}
          {answered > 1 ? 'Elles vont' : 'Elle va'} servir à cadrer l’étude — vous serez peut-être
          recontacté·e pour préciser un point.
        </p>
        <div className="m0-thanks-actions">
          <Button variant="ghost" onClick={close}>Fermer</Button>
          <Button variant="ghost" onClick={onEdit}>Modifier mes réponses</Button>
        </div>
        {closed ? <p className="zy-texte-muet" role="status">Vous pouvez fermer cet onglet.</p> : null}
      </section>

      <section className="m0-access" aria-labelledby="m0-access-title">
        <Eyebrow>Pour aller plus loin</Eyebrow>
        <h2 className="t-h3" id="m0-access-title">Suivre la recherche sur Zygosight</h2>
        <p className="card-lede">
          Un accès au référentiel du projet vous permet de consulter les études, les enseignements et
          l’Adoption Score au fil de la recherche. Laissez votre adresse : {sender} validera votre accès et
          vous enverra un lien pour créer votre compte.
        </p>
        {request.isSuccess ? (
          <p className="m0-public-ok" role="status">
            Demande transmise. Vous recevrez un lien par e-mail dès qu’elle sera validée.
          </p>
        ) : (
          <form className="m0-access-form" onSubmit={ask} noValidate>
            <Field
              label="Votre e-mail professionnel"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="claire@entreprise.fr"
              autoComplete="email"
            />
            <Button type="submit" variant="ghost" loading={request.isPending} disabled={!email.trim()}>
              Demander un accès
            </Button>
          </form>
        )}
        {request.error ? (
          <ErrorBanner>{request.error instanceof ApiRequestError ? request.error.message : 'La demande a échoué.'}</ErrorBanner>
        ) : null}
      </section>
      <LegalFooter compact />
    </main>
  );
}
